"use client"

import React, { ReactNode, RefObject, useCallback, useEffect, useRef, useState } from "react"

import { useWindowResize } from "@/hooks/useWindowResize"
import LeftArrow from "@layout/icons/LeftArrow"
import RightArrow from "@layout/icons/RightArrow"
import Row from "@ui-library/grid/Row"
import { cn, findColorName } from "@utils/utils"

interface ReviewCarouselSectionProps {
  title?: string
  background?: string
  cards: ReactNode[]
  autoRotate?: boolean
  rotationInterval?: number
  disclaimer?: string
}

const DEFAULT_ROTATION_INTERVAL = 5000
const PAUSE_DURATION = 10000

export const ReviewCarouselSection = ({
  title,
  background,
  cards,
  autoRotate = false,
  rotationInterval = DEFAULT_ROTATION_INTERVAL,
  disclaimer
}: ReviewCarouselSectionProps) => {
  const bg = findColorName(background)
  const containerWrapperRef: RefObject<HTMLDivElement | null> = useRef(null)
  const itemRef: RefObject<HTMLDivElement | null> = useRef(null)
  const [index, setIndex] = useState(0)
  const [isSliding, setIsSliding] = useState(false)
  const [visibleCards, setVisibleCards] = useState(1)
  const [hasOverflow, setHasOverflow] = useState(false)
  const [isPaused, setIsPaused] = useState(false)
  const pauseTimeoutRef = useRef<NodeJS.Timeout | null>(null)

  const threshold = 50
  const left = -1
  const right = 1
  let startX: number

  const scrollToIndex = useCallback(
    (targetIndex: number) => {
      if (containerWrapperRef.current && itemRef.current) {
        const container = containerWrapperRef.current
        const cardWidth = itemRef.current.getBoundingClientRect().width
        const expectedPosition = targetIndex * cardWidth
        const maxScroll = container.scrollWidth - container.clientWidth
        container.scrollLeft = Math.min(expectedPosition, maxScroll)
      }
    },
    [containerWrapperRef, itemRef]
  )

  const handleArrowClick = useCallback(
    (direction: number) => {
      if (isSliding) return

      const maxIndex = cards.length - visibleCards
      const newIndex = index + direction

      if (containerWrapperRef.current && itemRef.current && newIndex <= maxIndex && newIndex >= 0) {
        setIsSliding(true)
        const container = containerWrapperRef.current
        const cardWidth = itemRef.current.getBoundingClientRect().width
        const newScrollLeft = container.scrollLeft + cardWidth * direction
        const maxScroll = container.scrollWidth - container.clientWidth
        container.scrollLeft = Math.min(newScrollLeft, maxScroll)
        setIndex(newIndex)

        setTimeout(() => {
          setIsSliding(false)
        }, 300)
      }
    },
    [isSliding, cards.length, visibleCards, index]
  )

  const pauseRotation = useCallback(() => {
    if (!autoRotate) return

    setIsPaused(true)

    if (pauseTimeoutRef.current) {
      clearTimeout(pauseTimeoutRef.current)
    }

    pauseTimeoutRef.current = setTimeout(() => {
      setIsPaused(false)
    }, PAUSE_DURATION)
  }, [autoRotate])

  const handleManualNavigation = useCallback(
    (direction: number) => {
      pauseRotation()
      handleArrowClick(direction)
    },
    [pauseRotation, handleArrowClick]
  )

  const handleTouchStart = (event: React.TouchEvent<HTMLDivElement>) => {
    startX = event.touches[0].pageX
    pauseRotation()
  }

  const handleTouchEnd = (event: React.TouchEvent<HTMLDivElement>) => {
    const endX = event.changedTouches[0].pageX
    const difference = startX - endX

    if (difference > threshold) {
      handleArrowClick(right)
    } else if (difference < -threshold) {
      handleArrowClick(left)
    }
  }

  const handleMouseEnter = () => {
    if (autoRotate) {
      setIsPaused(true)
    }
  }

  const handleMouseLeave = () => {
    if (autoRotate) {
      setIsPaused(false)
    }
  }

  const updateVisibleCards = useCallback(() => {
    if (containerWrapperRef.current && itemRef.current) {
      const container = containerWrapperRef.current
      const containerWidth = container.getBoundingClientRect().width
      const cardWidth = itemRef.current.getBoundingClientRect().width
      const numVisibleCards = Math.floor(containerWidth / cardWidth)
      setVisibleCards(numVisibleCards)
      // Detect actual horizontal overflow (1px tolerance for sub-pixel rounding).
      // This is more reliable than deriving from floor(containerWidth / cardWidth).
      setHasOverflow(container.scrollWidth - container.clientWidth > 1)
    }
  }, [])

  useWindowResize(updateVisibleCards)

  // Calculate visible cards on mount
  useEffect(() => {
    updateVisibleCards()
  }, [updateVisibleCards])

  // Sync scroll position with index
  useEffect(() => {
    scrollToIndex(index)
  }, [index, scrollToIndex])

  // Auto-rotation effect
  useEffect(() => {
    if (!autoRotate || isPaused || cards.length <= visibleCards) return

    const maxIndex = cards.length - visibleCards

    const interval = setInterval(() => {
      if (!isSliding) {
        if (index >= maxIndex) {
          setIndex(0)
          scrollToIndex(0)
        } else {
          setIndex(prev => prev + 1)
        }
      }
    }, rotationInterval)

    return () => clearInterval(interval)
  }, [autoRotate, isPaused, index, visibleCards, isSliding, rotationInterval, cards.length, scrollToIndex])

  // Cleanup pause timeout on unmount
  useEffect(() => {
    return () => {
      if (pauseTimeoutRef.current) {
        clearTimeout(pauseTimeoutRef.current)
      }
    }
  }, [])

  return (
    <section
      className={cn(
        "relative mx-auto my-12 max-w-full overflow-x-clip md:my-16",
        bg === "surface" && "mx-0 rounded-3xl bg-surface py-12 md:mx-4 md:py-16"
      )}
    >
      {title && (
        <Row justifyContent="center" justifyContentMobile="center">
          <div className="mb-6 w-full px-4 text-left lg:mb-10 lg:max-w-3xl lg:text-center">
            <h2>{title}</h2>
          </div>
        </Row>
      )}

      <div
        className="carouselArea"
        onMouseEnter={handleMouseEnter}
        onMouseLeave={handleMouseLeave}
        role="region"
        aria-label={title || "Kundrecensioner"}
      >
        <div
          ref={containerWrapperRef}
          onTouchStart={handleTouchStart}
          onTouchEnd={handleTouchEnd}
          className="mb-8 box-border w-full overflow-x-hidden overflow-y-visible scroll-smooth py-4"
        >
          <Row className="flex-nowrap">
            {cards.map((card, idx) => (
              <ReviewCarouselItem isSingleCard={cards.length === 1} key={`${idx}-review-card`} ref={itemRef}>
                {card}
              </ReviewCarouselItem>
            ))}
          </Row>
        </div>
      </div>

      {cards.length > 1 && hasOverflow && (
        <div className="flex justify-center gap-6">
          <button
            disabled={index === 0}
            onClick={() => handleManualNavigation(left)}
            aria-label="Föregående recension"
            className={cn(
              "flex w-full cursor-pointer justify-end whitespace-nowrap border-0 bg-transparent p-4 text-on-surface",
              index === 0 && "pointer-events-none cursor-default text-neutral-30"
            )}
          >
            <LeftArrow />
          </button>
          <button
            disabled={index >= cards.length - visibleCards}
            onClick={() => handleManualNavigation(right)}
            aria-label="Nästa recension"
            className={cn(
              "flex w-full cursor-pointer justify-start whitespace-nowrap border-0 bg-transparent p-4 text-on-surface",
              index >= cards.length - visibleCards && "pointer-events-none cursor-default text-neutral-30"
            )}
          >
            <RightArrow />
          </button>
        </div>
      )}

      {disclaimer && (
        <Row justifyContent="center" justifyContentMobile="center">
          <div className="mt-8 w-full px-4 lg:max-w-3xl">
            <p className="text-center text-sm text-neutral-50">{disclaimer}</p>
          </div>
        </Row>
      )}
    </section>
  )
}

export default ReviewCarouselSection

type ReviewCarouselItemProps = {
  children: React.ReactNode
  isSingleCard?: boolean
}

const ReviewCarouselItem = React.forwardRef<HTMLDivElement, ReviewCarouselItemProps>(
  ({ children, isSingleCard }, ref) => {
    return (
      <div
        ref={ref}
        className={cn(
          "m-[initial] flex-12 px-2 first:pl-4 last:pr-4 md:flex-6 lg:flex-4",
          isSingleCard && "m-auto flex-12 pr-0"
        )}
      >
        {children}
      </div>
    )
  }
)

ReviewCarouselItem.displayName = "ReviewCarouselItem"
